import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Container, Graphics, Text, Sprite } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { closeBetSelectPopup, setBet } from "../../../../State/actions";
import {
  selectBetSelectPopupActive,
  selectBets,
  selectBet,
  selectBalance,
  selectCurrency,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../textureConfigs";
import { ASSET_NAME } from "../../../../assets";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import BetSelectContent from "./BetSelectContent";
import anime from "animejs";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";

// Configuration for popup (fullscreen mode)
const POPUP_CONFIG = {
  // Base values in pixels (will be scaled by uiSymbolScale)
  basePadding: {
    horizontal: 36,
    vertical: 16,
  },
  baseTitleFontSize: 48,
  baseTitleGap: 48,
  baseScrollbarOffset: 0.5,

  // Multipliers for each device/orientation combination
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 2.0, v: 7.0 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.0,
      scrollbarOffsetMultiplier: 1.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 10.0, v: 6.0 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 1.5,
      scrollbarOffsetMultiplier: 1.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
      titleFontMultiplier: 2.0,
      titleGapMultiplier: 2.25,
      scrollbarOffsetMultiplier: 2.0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 1.5 }, // Match autoplay popup
      titleFontMultiplier: 1.15, // Match autoplay popup
      titleGapMultiplier: 0.75, // Match autoplay popup
      scrollbarOffsetMultiplier: 0,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetLeft: 0.95, // Offset from left edge (left side of toolbar)
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.0, v: 1.0 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 1.0,
      scrollbarOffsetMultiplier: 1.0,
      // Windowed mode positioning offsets (as multipliers of popup size)
      positionOffsetLeft: 1.05, // Offset from left edge (left side of toolbar)
      positionOffsetBottom: 2.25, // Offset from bottom (in closeButtonConfig.height units)
    },
  },
};

const BetSelectPopup = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBetSelectPopupActive);
  const bets = useSelector(selectBets);
  const currentBet = useSelector(selectBet);
  const balance = useSelector(selectBalance);
  const currency = useSelector(selectCurrency);

  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    getResponsiveConfig,
  } = useDimensions();

  const containerRef = useRef(null);
  const animationRef = useRef(null);

  const [selectedBet, setSelectedBet] = useState(currentBet);

  const handleClose = useCallback(() => {
    dispatch(closeBetSelectPopup());
  }, [dispatch]);

  const handleBetSelect = useCallback(
    (betValue) => {
      soundManager.play(SOUND_NAME.button_click);
      setSelectedBet(betValue);
      dispatch(setBet(betValue));
      dispatch(closeBetSelectPopup());
    },
    [dispatch]
  );

  // Note: Desktop now shows windowed popup when clicking BetDisplay

  // Reset selected bet when popup opens
  useEffect(() => {
    if (popupActive) {
      setSelectedBet(currentBet);
    }
  }, [popupActive, currentBet]);

  // Windowed mode for tablet landscape and desktop
  const showPopupFrame =
    (device === PixiSettings.DEVICE.TABLET &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE) ||
    device === PixiSettings.DEVICE.DESKTOP;

  // Get button background texture for natural aspect ratio
  const buttonBackgroundTexture = PIXI.Assets.get(
    ASSET_NAME.popi_button_bet_background
  );
  const buttonAspectRatio = buttonBackgroundTexture
    ? buttonBackgroundTexture.width / buttonBackgroundTexture.height
    : 2.0; // Fallback to 2:1

  // Get popup frame texture - use horizontal frame for tablet landscape
  const popupFrameTexture = PIXI.Assets.get(
    showPopupFrame
      ? ASSET_NAME.popi_popup_frame_horizontal
      : ASSET_NAME.popi_popup_frame_settings
  );

  // Popup size and scale calculation
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) return { width: 0, height: 0, scale: 1 };

    if (showPopupFrame) {
      // Windowed mode: Use native texture size scaled by uiSymbolScale and multiplier
      // Adjust multipliers to change popup size per device
      const sizeMultiplier = device === PixiSettings.DEVICE.DESKTOP ? 0.575 : 0.92;

      const finalScale = uiSymbolScale * sizeMultiplier;
      return {
        width: popupFrameTexture.width * finalScale,
        height: popupFrameTexture.height * finalScale,
        scale: finalScale,
      };
    }

    // Fullscreen mode
    return {
      width: dimensions.width,
      height: dimensions.height,
      scale: uiSymbolScale,
    };
  }, [popupFrameTexture, showPopupFrame, dimensions, uiSymbolScale, device]);

  const popupScale = popupSize.scale;

  // Viewport configuration
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) return null;

    if (showPopupFrame) {
      // Windowed mode (Tablet Landscape): Use config for consistency
      const config = POPUP_CONFIG[device]?.[orientation] || {
        paddingMultiplier: { h: 1.0, v: 1.0 },
        titleFontMultiplier: 1.0,
        titleGapMultiplier: 1.0,
        scrollbarOffsetMultiplier: 1.0,
      };

      const padding =
        POPUP_CONFIG.basePadding.horizontal *
        config.paddingMultiplier.h *
        popupScale;
      const contentWidth = popupSize.width - padding * 2;
      const contentHeight = popupSize.height - padding * 2;
      const titleFontSize = Math.floor(
        POPUP_CONFIG.baseTitleFontSize * config.titleFontMultiplier * popupScale
      );
      const titleGap =
        POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * popupScale;
      const titleYOffset = titleFontSize + titleGap;

      return {
        maxWidth: contentWidth,
        maxHeight: contentHeight,
        x: -popupSize.width / 2 + padding,
        y: -popupSize.height / 2 + padding,
        titleFontSize,
        titleYOffset,
        viewportHeight: contentHeight - titleYOffset,
        scrollbarOffset:
          POPUP_CONFIG.baseScrollbarOffset *
          config.scrollbarOffsetMultiplier *
          popupScale,
      };
    }

    // Fullscreen mode: Get config for current device/orientation
    const config = POPUP_CONFIG[device]?.[orientation] || {
      paddingMultiplier: { h: 1.0, v: 1.0 },
      titleFontMultiplier: 1.0,
      titleGapMultiplier: 1.0,
      scrollbarOffsetMultiplier: 1.0,
    };

    // Calculate padding, font size, and gap with multipliers (all scaled by uiSymbolScale)
    const paddingH =
      POPUP_CONFIG.basePadding.horizontal *
      config.paddingMultiplier.h *
      uiSymbolScale;
    const paddingV =
      POPUP_CONFIG.basePadding.vertical *
      config.paddingMultiplier.v *
      uiSymbolScale;
    const contentWidth = dimensions.width - paddingH * 2;
    const contentHeight = dimensions.height - paddingV * 2;
    const titleFontSize = Math.floor(
      POPUP_CONFIG.baseTitleFontSize *
        config.titleFontMultiplier *
        uiSymbolScale
    );
    const titleGap =
      POPUP_CONFIG.baseTitleGap * config.titleGapMultiplier * uiSymbolScale;
    const titleYOffset = titleFontSize + titleGap;

    return {
      maxWidth: contentWidth,
      maxHeight: contentHeight,
      x: -dimensions.width / 2 + paddingH,
      y: -dimensions.height / 2 + paddingV,
      titleFontSize,
      titleYOffset,
      viewportHeight: contentHeight - titleYOffset,
      scrollbarOffset:
        POPUP_CONFIG.baseScrollbarOffset * config.scrollbarOffsetMultiplier,
    };
  }, [
    popupSize,
    dimensions,
    popupFrameTexture,
    showPopupFrame,
    popupScale,
    uiSymbolScale,
    device,
    orientation,
  ]);

  // Button configuration
  const betButtonConfig = useMemo(() => {
    if (!viewportConfig)
      return {
        width: 100,
        height: 50,
        fontSize: 18,
        spacing: 10,
        rowSpacing: 10,
        columns: 3,
      };

    // Columns: 5 for windowed mode, 5 for mobile landscape, 3 for mobile portrait and tablet portrait
    const columns = showPopupFrame
      ? 5
      : device === PixiSettings.DEVICE.MOBILE &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE
      ? 5
      : 3;
    const gapRatio = 0.075;

    if (showPopupFrame) {
      // Windowed mode: Calculate to fill content area (reserve space for scrollbar) using natural aspect ratio
      const scrollbarWidth = 36 * popupScale; // Scrollbar width
      const availableWidth = viewportConfig.maxWidth - scrollbarWidth;
      const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
      const buttonHeight = buttonWidth / buttonAspectRatio; // Use natural aspect ratio
      const spacing = buttonWidth * gapRatio;
      const rowSpacing = spacing; // Same gap for both horizontal and vertical

      return {
        width: buttonWidth,
        height: buttonHeight,
        fontSize: Math.floor(buttonHeight * 0.5),
        spacing,
        rowSpacing,
        columns,
      };
    }

    // Fullscreen mode: Account for scrollbar width
    const scrollbarWidth = 36 * uiSymbolScale; // Scrollbar width
    const availableWidth = viewportConfig.maxWidth - scrollbarWidth;
    const buttonWidth = availableWidth / (columns + (columns - 1) * gapRatio);
    const buttonHeight = buttonWidth / buttonAspectRatio; // Use natural aspect ratio
    const spacing = buttonWidth * gapRatio;
    const rowSpacing = spacing; // Same gap

    return {
      width: buttonWidth,
      height: buttonHeight,
      fontSize: Math.floor(buttonHeight * 0.5),
      spacing,
      rowSpacing,
      columns,
    };
  }, [
    showPopupFrame,
    viewportConfig,
    popupScale,
    buttonAspectRatio,
    uiSymbolScale,
    device,
    orientation,
  ]);

  const buttonSpacing = betButtonConfig.spacing;
  const rowSpacing = betButtonConfig.rowSpacing;
  const gridColumns = betButtonConfig.columns;

  // Title text style
  const titleStyle = useMemo(() => {
    if (!viewportConfig) return null;
    return new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: viewportConfig.titleFontSize,
      fontWeight: "500",
      fill: 0xffffff,
      align: "center",
    });
  }, [viewportConfig]);

  // Fade in/out animation
  useEffect(() => {
    if (containerRef.current) {
      if (popupActive) {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      } else {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 0,
          duration: 200,
          easing: "easeInQuad",
        });
      }
    }
  }, [popupActive]);

  // Get button config for close button positioning
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Get button config for close button positioning
  const closeButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  /**
   * Popup container position calculation
   */
  const popupContainerPosition = useMemo(() => {
    if (showPopupFrame && toolbar && toolbar.width && popupSize.width) {
      // Get config for current device/orientation
      const config = POPUP_CONFIG[device]?.[orientation] || {
        positionOffsetLeft: 1.25,
        positionOffsetBottom: 2.25,
      };

      // Position popup above toolbar on LEFT side using configurable offsets
      const toolbarLeftEdge = (dimensions.width - toolbar.width) / 2;
      const offsetFromLeft = popupSize.width * config.positionOffsetLeft;
      const offsetFromBottom =
        popupSize.height * 0.5 +
        closeButtonConfig.height * config.positionOffsetBottom;

      return {
        x: toolbarLeftEdge + offsetFromLeft,
        y: dimensions.height - offsetFromBottom,
      };
    }
    // Fullscreen - centered
    return {
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    };
  }, [
    showPopupFrame,
    dimensions,
    toolbar,
    popupSize,
    closeButtonConfig,
    device,
    orientation,
  ]);

  // Close button position - aligned to padding edges (top-right)
  const closeButtonPosition = useMemo(() => {
    if (!viewportConfig) return { x: 0, y: 0 };

    // Close button has anchor 0.5, so offset by half its size to align with padding
    const buttonSize = buttonConfig.width * (showPopupFrame ? 0.5 : 0.85);
    const relativeX = viewportConfig.maxWidth - buttonSize / 2;
    // Align vertically with title center: titleFontSize / 2 centers the button with the title
    const relativeY = viewportConfig.titleFontSize / 2;

    return {
      x: popupContainerPosition.x + viewportConfig.x + relativeX,
      y: popupContainerPosition.y + viewportConfig.y + relativeY,
    };
  }, [viewportConfig, popupContainerPosition, buttonConfig, showPopupFrame]);

  if (!popupFrameTexture || !viewportConfig || !titleStyle) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      name="secondary_bet_select_overlay"
      zIndex={10}
      eventMode={popupActive ? "static" : "none"}
    >
      {/* Gray Background - only for fullscreen (mobile/tablet portrait) */}
      {!showPopupFrame && (
        <Graphics
          name="secondary_bet_select_background"
          draw={(g) => {
            g.clear();
            g.beginFill(0x161616, 1);
            g.drawRect(0, 0, dimensions.width, dimensions.height);
            g.endFill();
          }}
          eventMode={popupActive ? "static" : "none"}
          onpointertap={(e) => e.stopPropagation()}
        />
      )}

      {/* Popup Container */}
      <Container
        name="secondary_bet_select_popup"
        x={popupContainerPosition.x}
        y={popupContainerPosition.y}
        eventMode="static"
      >
        {/* Popup Frame Sprite (Tablet Landscape only) */}
        {showPopupFrame && (
          <Sprite
            texture={popupFrameTexture}
            anchor={0.5}
            width={popupSize.width}
            height={popupSize.height}
          />
        )}

        {/* Content Viewport Container */}
        <Container
          name="secondary_bet_select_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
        >
          {/* Title */}
          <Text
            text={t("total_bet")}
            style={titleStyle}
            anchor={{ x: 0, y: 0 }}
            x={0}
            y={0}
          />

          {/* Scrollable Content */}
          <Container
            name="scrollable_content_container"
            y={viewportConfig.titleYOffset}
          >
            <BetSelectContent
              bets={bets}
              selectedBet={selectedBet}
              balance={balance}
              currency={currency}
              onBetSelect={handleBetSelect}
              viewportConfig={viewportConfig}
              gridColumns={gridColumns}
              betButtonConfig={betButtonConfig}
              buttonSpacing={buttonSpacing}
              rowSpacing={rowSpacing}
              leftAlign={showPopupFrame}
            />
          </Container>
        </Container>
      </Container>

      {/* Close Button */}
      <ButtonMenuClose
        onClose={handleClose}
        x={closeButtonPosition.x}
        y={closeButtonPosition.y}
        isSettings={true}
        sizeMultiplier={showPopupFrame ? 0.5 : 0.85}
      />
    </Container>
  );
};

export default BetSelectPopup;
